/**
 * The batch summary of a Files-page upload (reliability plan 6.5).
 *
 * One summary per batch: how many files were stored, indexed, failed and
 * skipped as duplicates, a downloadable list of the failures, the sentence
 * that the user may leave while indexing goes on, and a notification when the
 * batch has finished indexing.
 *
 * Why this is a module and not part of dbfile.vue: the upload queue outlives
 * the page (file.ts is a module singleton that keeps draining across route
 * changes), and so does indexing, which runs on the server for minutes or hours
 * after the last byte. The summary therefore lives here, keyed by project, and
 * the page binds to it: it shows the current one on mount, the counts move
 * while it is open, and the finish notification fires from whichever page the
 * user is on when the last file settles.
 *
 * DOM-free apart from the two browser APIs it exists to use, both read at call
 * time and injectable for tests: the Notification API and localStorage (to ask
 * for notifications once). Vue's `reactive` is the only framework dependency,
 * the same one file.ts already carries.
 *
 * How "indexed" is known. The page's index set-up reports per file what it did
 * (dispatched, not needed, failed); for the dispatched files the watch asks the
 * marker sweep file.ts already runs (`run::` records and `done::` markers, one
 * records query per look, never one request per file) until every file of the
 * batch has a verdict. See applyIndexSweep for the rule.
 */
import { reactive } from "vue";
import { showToast } from "@/components/toast";
import { track } from "@/code/analytics";
import { PERSIST_MAX_TOTAL_BYTES } from "./upload_durability";

export type BatchFailure = {
    /** The storage path the file would have had. */
    name: string;
    code: string;
    message: string;
    /** Where it failed: the upload itself, or the indexing after it. */
    stage: "upload" | "index";
};

export type UploadBatchSummary = {
    serviceId: string;
    serviceName: string;
    /** When the first file was queued. */
    startedAt: number;
    /** When every upload had settled. 0 while the batch uploads. */
    settledAt: number;
    stored: number;
    skipped: number;
    reindexed: number;
    duplicates: number;
    /** Files queued without a durable copy (upload_durability's ceiling). */
    unmirrored: number;
    failed: BatchFailure[];
    /** Storage paths handed to indexing and not yet finished. */
    pending: { [storagePath: string]: true };
    pendingCount: number;
    /** Of the pending paths, those whose indexing request was accepted (or
     *  which another run already owned). Only these can be judged by the
     *  marker sweep: a path still waiting for its set-up may carry a stale
     *  marker from an earlier life (a Reindex of an indexed file). */
    dispatched: { [storagePath: string]: true };
    indexed: number;
    /** Files the set-up had nothing to do for: the project has no agent. */
    notIndexed: number;
    /** When the last pending file settled. 0 while indexing runs; equals
     *  settledAt when nothing was handed to indexing. */
    indexingFinishedAt: number;
    /** The page has shown this summary. */
    seen: boolean;
    notified: boolean;
};

/** The newest batch of each project, what the Files page binds to. */
export const batchSummaries: { [serviceId: string]: UploadBatchSummary } = reactive({});

/** Every batch of a project that still has work in flight (uploading, or
 *  indexing), newest last. Outcomes are routed to whichever batch holds the
 *  path, because a second batch can start while the first is still indexing. */
const liveBatches: { [serviceId: string]: UploadBatchSummary[] } = {};

const uploadFailure = (f: { name: string; code: string; message: string }): BatchFailure =>
    ({ name: f.name, code: f.code || "", message: f.message || "", stage: "upload" });

/** A fresh batch for this project replaces the summary on show. The previous
 *  batch keeps its own watch until its indexing is done (see finishIndexing). */
export function startBatchSummary(serviceId: string, serviceName = "", now = Date.now()): UploadBatchSummary {
    const key = serviceId || "";
    batchSummaries[key] = {
        serviceId: key,
        serviceName: serviceName || "",
        startedAt: now,
        settledAt: 0,
        stored: 0,
        skipped: 0,
        reindexed: 0,
        duplicates: 0,
        unmirrored: 0,
        failed: [],
        pending: {},
        pendingCount: 0,
        dispatched: {},
        indexed: 0,
        notIndexed: 0,
        indexingFinishedAt: 0,
        seen: false,
        notified: false,
    };
    const s = batchSummaries[key];
    const live = liveBatches[key] || (liveBatches[key] = []);
    // Batches that are over leave the live list here, so it never grows past
    // the handful that can still receive an outcome.
    for (let i = live.length - 1; i >= 0; i--) {
        if (live[i].indexingFinishedAt) live.splice(i, 1);
    }
    live.push(s);
    return s;
}

export function currentBatchSummary(serviceId: string): UploadBatchSummary | null {
    return batchSummaries[serviceId || ""] || null;
}

/** The live batch of a project that holds this path, newest first. */
function batchHolding(serviceId: string, storagePath: string): UploadBatchSummary | null {
    const live = liveBatches[serviceId || ""];
    if (!live) return null;
    for (let i = live.length - 1; i >= 0; i--) {
        if (live[i].pending[storagePath]) return live[i];
    }
    return null;
}

/** file.ts: this file's bytes are stored (or kept) and its set-up is starting. */
export function noteHandedToIndex(serviceId: string, storagePath: string): void {
    const s = batchSummaries[serviceId || ""];
    if (!s || !storagePath || s.pending[storagePath]) return;
    s.pending[storagePath] = true;
    s.pendingCount++;
}

/** upload_durability passed these files over: no durable copy. */
export function noteUnmirrored(serviceId: string, count: number): void {
    const s = batchSummaries[serviceId || ""];
    if (!s || !(count > 0)) return;
    s.unmirrored += count;
}

/** The page: the indexing request for this file was accepted, or another run
 *  already owns the file. From now on the marker sweep decides. */
export function noteIndexDispatched(serviceId: string, storagePath: string): void {
    const s = batchHolding(serviceId, storagePath);
    if (s) s.dispatched[storagePath] = true;
}

/** The page: nothing to index (the project has no agent). */
export function noteIndexNotNeeded(serviceId: string, storagePath: string, now = Date.now()): void {
    const s = batchHolding(serviceId, storagePath);
    if (!s) return;
    settlePending(s, storagePath);
    s.notIndexed++;
    maybeFinishIndexing(s, now);
}

/** The page: the set-up gave up on this file. */
export function noteIndexFailed(serviceId: string, storagePath: string, err: { code?: string; message?: string }, now = Date.now()): void {
    const s = batchHolding(serviceId, storagePath);
    if (!s) return;
    settlePending(s, storagePath);
    s.failed.push({ name: storagePath, code: err?.code || "", message: err?.message || "Indexing could not be requested.", stage: "index" });
    maybeFinishIndexing(s, now);
}

function settlePending(s: UploadBatchSummary, storagePath: string): void {
    if (!s.pending[storagePath]) return;
    delete s.pending[storagePath];
    delete s.dispatched[storagePath];
    s.pendingCount--;
}

/**
 * file.ts: every upload of the batch has settled. The counts are final; the
 * failures so far are the upload ones, index failures keep arriving. Starts
 * the indexing watch when anything was handed to indexing.
 */
export function finishBatchUploads(
    serviceId: string,
    counts: { stored: number; skipped: number; reindexed: number; duplicates: number; failed: Array<{ name: string; code: string; message: string }> },
    now = Date.now(),
): UploadBatchSummary | null {
    const s = batchSummaries[serviceId || ""];
    if (!s) return null;
    s.stored = counts.stored;
    s.skipped = counts.skipped;
    s.reindexed = counts.reindexed;
    s.duplicates = counts.duplicates;
    s.failed = counts.failed.map(uploadFailure).concat(s.failed.filter((f) => f.stage === "index"));
    s.settledAt = now;
    if (s.pendingCount === 0) {
        finishIndexing(s, now);
    } else {
        startBatchWatch(s);
    }
    return s;
}

function maybeFinishIndexing(s: UploadBatchSummary, now: number): void {
    if (s.settledAt && s.pendingCount === 0 && !s.indexingFinishedAt) finishIndexing(s, now);
}

/** Files whose indexing failed, as opposed to their upload. */
export const indexFailures = (s: UploadBatchSummary): BatchFailure[] => s.failed.filter((f) => f.stage === "index");

/** Something of this batch was handed to a run: there was indexing to wait for. */
export function hadIndexing(s: UploadBatchSummary): boolean {
    return s.indexed > 0 || indexFailures(s).length > 0 || Object.keys(s.dispatched).length > 0;
}

function finishIndexing(s: UploadBatchSummary, now: number): void {
    if (s.indexingFinishedAt) return;
    s.indexingFinishedAt = now;
    const live = liveBatches[s.serviceId];
    if (live) {
        const i = live.indexOf(s);
        if (i !== -1) live.splice(i, 1);
    }
    // A batch nothing was indexed for (no agent, or every file failed to
    // upload) has nothing to announce: the summary on the page said it all.
    if (!hadIndexing(s)) return;
    const notify = (watchDeps && watchDeps.notify) || notifyBatchIndexed;
    try { notify(s); } catch (e) { /* a notification must not break the queue */ }
    // One event per batch, counts only: files indexed, files whose indexing
    // failed, and how long the whole batch took from the first file queued.
    track("upload_batch_indexed", {
        indexed: String(s.indexed),
        failed: String(indexFailures(s).length),
        minutes: String(Math.max(0, Math.round((now - s.startedAt) / 60_000))),
    });
}

// ─── the marker sweep rule ───────────────────────────────────────────────────

/** The shape of file.ts's IndexMarkerSweep this module reads. Structural, so
 *  there is no import cycle and a test can hand in a literal. */
export type IndexSweepLike = {
    done: Set<string>;
    runs: Map<string, { status: string; started?: number; finished?: number; error?: string }>;
    partial?: boolean;
};

/** A verdict older than this before the batch began belongs to a previous life
 *  of the path; the new run has not written yet. */
export const RUN_END_SLACK_MS = 5 * 60 * 1000;

/**
 * Settle what the sweep can settle. Only DISPATCHED paths are judged. A `run::`
 * record ending after the batch began decides (done, error, cancelled); a
 * `working` one keeps the file pending; with no record, a `done::` marker is
 * completion (the dispatch deleted the previous life's marker, so one that is
 * present now belongs to this run). Returns how many files this look settled.
 */
export function applyIndexSweep(s: UploadBatchSummary, sweep: IndexSweepLike, now = Date.now()): number {
    let settled = 0;
    const paths = Object.keys(s.pending);
    for (const path of paths) {
        if (!s.dispatched[path]) continue;
        const run = sweep.runs && typeof sweep.runs.get === "function" ? sweep.runs.get(path) : undefined;
        if (run) {
            if (run.status === "working") continue;
            const endedAt = typeof run.finished === "number" ? run.finished : run.started;
            if (typeof endedAt === "number" && endedAt < s.startedAt - RUN_END_SLACK_MS) continue;
            if (run.status === "done") {
                settlePending(s, path);
                s.indexed++;
                settled++;
            } else if (run.status === "error" || run.status === "cancelled") {
                settlePending(s, path);
                s.failed.push({
                    name: path,
                    code: "",
                    message: run.status === "cancelled" ? "Indexing was stopped." : (run.error || "Indexing failed."),
                    stage: "index",
                });
                settled++;
            }
            continue;
        }
        if (sweep.done && sweep.done.has(path)) {
            settlePending(s, path);
            s.indexed++;
            settled++;
        }
    }
    if (settled) maybeFinishIndexing(s, now);
    return settled;
}

// ─── the watch ───────────────────────────────────────────────────────────────

export type BatchWatchDeps = {
    /** file.ts's sweepIndexMarkers. */
    sweep: (serviceId: string) => Promise<IndexSweepLike | null>;
    /** file.ts's invalidateDoneMarkerSweep, so each look is fresh. */
    invalidate?: (serviceId: string) => void;
    now?: () => number;
    schedule?: (fn: () => void, ms: number) => unknown;
    /** Defaults to notifyBatchIndexed. */
    notify?: (s: UploadBatchSummary) => void;
};

let watchDeps: BatchWatchDeps | null = null;

/** file.ts installs the sweep; tests install fakes. Null turns the watch off. */
export function configureBatchWatch(deps: BatchWatchDeps | null): void {
    watchDeps = deps;
}

/** First look after the uploads settle, then once a minute. The sweep is one
 *  records query per look, the same the Files page's badges run. */
export const BATCH_WATCH_FIRST_MS = 20_000;
export const BATCH_WATCH_POLL_MS = 60_000;
/** A batch still unresolved after this is left alone. */
export const BATCH_WATCH_MAX_MS = 24 * 60 * 60 * 1000;

/** setTimeout that never keeps a Node process alive (tests, scripts); in a
 *  browser `unref` does not exist and the timer is an ordinary one. */
function defaultSchedule(fn: () => void, ms: number): unknown {
    const t: any = setTimeout(fn, ms);
    if (t && typeof t.unref === "function") t.unref();
    return t;
}

function startBatchWatch(s: UploadBatchSummary): void {
    const deps = watchDeps;
    if (!deps || typeof deps.sweep !== "function") return;
    const schedule = deps.schedule || defaultSchedule;
    const now = deps.now || Date.now;
    const tick = async () => {
        if (s.indexingFinishedAt) return;
        if (s.pendingCount > 0) {
            try {
                if (deps.invalidate) deps.invalidate(s.serviceId);
                const sweep = await deps.sweep(s.serviceId);
                if (sweep) applyIndexSweep(s, sweep, now());
            } catch (e) {
                // The next look retries; the batch is not harmed by a failed read.
            }
        }
        if (s.indexingFinishedAt) return;
        if (s.pendingCount === 0) {
            finishIndexing(s, now());
            return;
        }
        if (now() - s.settledAt > BATCH_WATCH_MAX_MS) return;
        schedule(tick, BATCH_WATCH_POLL_MS);
    };
    schedule(tick, BATCH_WATCH_FIRST_MS);
}

// ─── the sentences ───────────────────────────────────────────────────────────

export function summaryLine(s: UploadBatchSummary): string {
    return `${s.stored} stored, ${s.indexed} indexed, ${s.failed.length} failed, ${s.duplicates} skipped as duplicates`;
}

export const LEAVE_PAGE_LINE = "You can leave this page. Indexing continues on the server.";
export const INDEXING_FINISHED_LINE = "Indexing has finished.";

/** The ceiling as the user reads it: "512 MB". */
export const MIRROR_CAP_LABEL = `${Math.round(PERSIST_MAX_TOTAL_BYTES / (1024 * 1024))} MB`;

export function unmirroredLine(count: number): string {
    return `${count} file(s) were queued without a saved copy, because this browser keeps at most ${MIRROR_CAP_LABEL} of copies: they upload as usual, but an automatic resume after a reload is not available for them.`;
}

export function indexingFinishedMessage(s: UploadBatchSummary): string {
    const where = s.serviceName ? ` for ${s.serviceName}` : "";
    return `Indexing finished${where}: ${s.indexed} indexed, ${s.failed.length} failed.`;
}

/** One failure as a line of the downloadable list: the path, a tab, the reason. */
export function failedListLine(f: BatchFailure): string {
    const stage = f.stage === "index" ? "indexing failed" : "upload failed";
    const reason = f.code && f.message ? `${f.code}: ${f.message}` : (f.code || f.message || "");
    return `/${f.name}\t${stage}${reason ? `: ${reason}` : ""}`;
}

/** The whole list, one file per line, made on the client. */
export function failedListText(s: UploadBatchSummary): string {
    return s.failed.map(failedListLine).join("\n") + (s.failed.length ? "\n" : "");
}

const two = (n: number) => (n < 10 ? "0" + n : String(n));

export function failedListFileName(d: Date = new Date()): string {
    return `failed-uploads-${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}-${two(d.getHours())}${two(d.getMinutes())}.txt`;
}

// ─── notifications ───────────────────────────────────────────────────────────

/** The Notification constructor as this module needs it. */
export type NotificationApi = {
    permission: string;
    requestPermission: () => Promise<string> | string;
    new (title: string, options?: { body?: string; tag?: string }): unknown;
};

const globalNotification = (): NotificationApi | null => {
    try {
        const n = (globalThis as any).Notification;
        return n && typeof n.requestPermission === "function" ? (n as NotificationApi) : null;
    } catch {
        return null;
    }
};

const globalStorage = (): Storage | null => {
    try { return (globalThis as any).localStorage || null; } catch { return null; }
};

export type NotificationState = "unsupported" | "default" | "granted" | "denied";

export function notificationPermission(api: NotificationApi | null = globalNotification()): NotificationState {
    if (!api) return "unsupported";
    const p = api.permission;
    return p === "granted" || p === "denied" ? p : "default";
}

/** Asked once per browser, from the button on the summary, never unprompted. */
export const NOTIFY_ASKED_KEY = "bq-upload-notify-asked";

export function notificationsAskedBefore(storage: Storage | null = globalStorage()): boolean {
    try { return !!storage && storage.getItem(NOTIFY_ASKED_KEY) === "1"; } catch { return false; }
}

/** Whether the summary should show the "notify me" button. */
export function canOfferNotifications(api: NotificationApi | null = globalNotification(), storage: Storage | null = globalStorage()): boolean {
    return notificationPermission(api) === "default" && !notificationsAskedBefore(storage);
}

/**
 * The button's click: ask the browser, remember that we asked, report how it
 * went (`target`: granted, denied or dismissed, the detail the tracking doc
 * names). The browser remembers a grant or a denial itself; a dismissed prompt
 * leaves the permission at "default", and the stored mark keeps the button
 * from coming back, so the question is asked once.
 */
export async function askForNotifications(
    api: NotificationApi | null = globalNotification(),
    storage: Storage | null = globalStorage(),
): Promise<NotificationState> {
    try { storage?.setItem(NOTIFY_ASKED_KEY, "1"); } catch { /* the button comes back next time; harmless */ }
    if (!api) return "unsupported";
    let result: string = "default";
    try {
        result = String(await Promise.resolve(api.requestPermission()));
    } catch {
        result = "default";
    }
    const state: NotificationState = result === "granted" || result === "denied" ? result : "default";
    track("upload_notify", { target: state === "default" ? "dismissed" : state });
    return state;
}

/**
 * Tell the user the batch has finished indexing: a browser notification when
 * they granted it, the site's own toast otherwise. Once per batch.
 */
export function notifyBatchIndexed(
    s: UploadBatchSummary,
    env?: { api?: NotificationApi | null; toast?: (message: string, type: "info", options?: { duration?: number }) => void },
): "notification" | "toast" | "none" {
    if (s.notified) return "none";
    s.notified = true;
    const message = indexingFinishedMessage(s);
    const api = env && "api" in env ? env.api : globalNotification();
    if (api && notificationPermission(api) === "granted") {
        try {
            new api("BunnyQuery", { body: message, tag: "bq-upload-" + s.serviceId });
            return "notification";
        } catch (e) {
            // Fall through to the toast: a constructor that throws (some
            // mobile browsers) must not swallow the message.
        }
    }
    const toast = (env && env.toast) || showToast;
    toast(message, "info", { duration: 10_000 });
    return "toast";
}
